import React, { useRef, useEffect, useLayoutEffect, useMemo, useState, useCallback, useDeferredValue } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Spin, Drawer, Modal, Input, Select, message as antMessage, Checkbox, Divider, Typography, List } from 'antd';
import { CrownOutlined, DownOutlined } from '@ant-design/icons';
import { useTheme } from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@stores/auth.store';
import {
    useChat,
    useSendMessage,
    useEditMessage,
    useDeleteMessage,
    useAddReaction,
    useRemoveReaction,
    useThreadReplies,
    useAutoMarkChatAsRead,
    useAutoMarkChatAsReadOnActivity,
    useForwardMessages,
    useEligibleChatUsers,
    useUpdateThreadTitle,
    useChatMessageSearch,
    useRenameChat,
    useAddChatMembers,
    useRemoveChatMember,
    canManageGroupChat,
} from '@hooks/chat/useChat';
import { useChatList } from '@hooks/chat/useChatList';
import { useChatSocket } from '@hooks/chat/useChatSocket';
import { useChatSocketStatus } from '@hooks/chat/useChatSocketStatus';
import { useChatTyping } from '@hooks/chat/useChatTyping';
import { useDocTitle } from '@hooks/useDocTitle';
import { emitTypingStart, emitTypingStop } from '@services/realtime.socket';
import { Container } from '@/components/ui/Container';
import { getAvatarColor } from '@utils/avatarColor';
import { resolveDmHeaderAvatarUrl, resolveChatAvatarUrl } from '@utils/chatAvatar';
import ChatCircleAvatar from '@/components/ui/ChatCircleAvatar';
import { MessageCompose, ChatHeader, ThreadTopBar, ChatMessageRow, ChatThreadParentMessageBody, ChatMessageAttachmentStack, ChatAssetsDrawer } from '@/components/module/chats';
import { usePublicTags } from '@hooks/public/useTags';
import {
    ChatFullHeightWrap,
    ChatLayout,
    ChatMessagePanel,
    ChatMessageList,
    ChatMessageListWrap,
    LoadMoreOverlay,
    TopBounceBadgeWrap,
    BottomNewMessageBadgeWrap,
    BottomJumpToLatestWrap,
    EmptyStateFill,
    DetailPanelLoading,
    TypingBubbleWrap,
    TypingBubble,
} from './style';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { plainTextPreviewFromChatBody } from '@utils/chatMessagePreviewText.util';

/** Get display name for a chat (DM: other participant(s), group: name or "Group") */
function getChatDisplayName(chat, currentUserId) {
    if (chat?.name) return chat.name;
    const participants = chat?.participants ?? [];
    const others = participants.filter((p) => String(p.userId) !== String(currentUserId));
    if (others.length === 0) return 'Chat';
    if (others.length === 1) return others[0].name || others[0].email || 'Unknown';
    return (
        others
            .map((p) => p.name || p.email)
            .filter(Boolean)
            .join(', ') || 'Group'
    );
}

/** Extract messages array from chat API response: { chat, messages: { items } } */
function getMessages(chatData) {
    if (!chatData) return [];
    const messagesObj = chatData.messages ?? chatData.data?.messages;
    const list = messagesObj?.items ?? chatData.items ?? (Array.isArray(chatData.messages) ? chatData.messages : []);
    const items = Array.isArray(list) ? list : [];

    // Backend can return thread replies nested under parent `replies`.
    // Flatten them so reply messages can render in the main list (and show the thread-title chip).
    const out = [];
    const seen = new Set();
    const push = (m) => {
        const id = m?.id ?? m?._id;
        if (!id) return;
        const k = String(id);
        if (seen.has(k)) return;
        seen.add(k);
        out.push(m);
    };

    items.forEach((m) => {
        push(m);
        const parentId = m?.id ?? m?._id;
        const replies = Array.isArray(m?.replies) ? m.replies : [];
        replies.forEach((r) => {
            // Ensure reply points back to its parent.
            const normalized = {
                ...r,
                parentMessageId: r?.parentMessageId ?? r?.parent_message_id ?? parentId ?? null,
            };
            push(normalized);
        });
    });

    return out;
}

/** Get chat object from API response (nested under .chat) */
function getChatFromResponse(chatData) {
    return chatData?.chat ?? chatData;
}

/** Get text from a message (type text content.text) */
function getMessageText(msg) {
    if (!msg) return '';
    if (msg.content?.text) return msg.content.text;
    if (typeof msg.content === 'string') return msg.content;
    return msg.body ?? msg.message ?? '';
}

function formatMessageTime(createdAt) {
    if (!createdAt) return '';
    const d = new Date(createdAt);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatChatDayLabel(createdAt) {
    if (!createdAt) return '';
    const d = new Date(createdAt);
    if (Number.isNaN(d.getTime())) return '';
    const today = new Date();
    const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const diffDays = Math.round((startOf(today) - startOf(d)) / (24 * 60 * 60 * 1000));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return d.toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' });
}

function dayKey(createdAt) {
    const d = new Date(createdAt);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getInitials(name) {
    if (!name) return '?';
    return name
        .split(/\s+/)
        .map((s) => s[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

/**
 * Resolve sender display name: from message payload, or from chat participants when message has no name (e.g. after send/socket).
 */
function getSenderDisplayName(msg, currentUserId, currentUserName, participants = []) {
    const senderId = msg.senderId ?? msg.userId ?? msg.createdBy;
    const own = String(senderId) === String(currentUserId);
    if (own) return currentUserName || 'You';
    const fromMsg = msg.senderName ?? msg.senderEmail ?? msg.userName ?? msg.sender?.name ?? msg.sender?.email;
    if (fromMsg) return fromMsg;
    const participant = participants.find((p) => String(p.userId ?? p.id) === String(senderId));
    return participant?.name ?? participant?.email ?? 'Unknown';
}

const Chat = () => {
    const DEV = typeof import.meta !== 'undefined' && import.meta.env?.DEV;
    const { chatId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const currentUser = useAuthStore((s) => s.user);
    const dark = theme?.colors?.background?.default === '#141414';
    const currentUserId = currentUser?.id ?? null;
    const queryClient = useQueryClient();
    const { tags: publicTags } = usePublicTags({ enabled: !!chatId });

    const { data: chatData, isLoading: messagesLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useChat(chatId);
    useChatSocket(chatId);
    useAutoMarkChatAsRead(chatId);
    const { typingUsers } = useChatTyping(chatId);
    const messages = useMemo(() => {
        // Messages are normalized (deduped + ascending) in `useChat` to avoid expensive sorts in the UI layer.
        return getMessages(chatData);
    }, [chatData]);
    useAutoMarkChatAsReadOnActivity(chatId, messages?.length);
    const deferredMessages = useDeferredValue(messages);

    /** Reply count and last reply time per parent message (for "N replies" below message) */
    const threadCountByParent = useMemo(() => {
        const map = {};
        (deferredMessages ?? []).forEach((m) => {
            const parentId = m.parentMessageId ?? m.parent_message_id;
            if (!parentId) return;
            if (!map[parentId]) map[parentId] = { count: 0, lastReplyAt: null };
            map[parentId].count += 1;
            const t = m.createdAt ?? m.created_at;
            if (t && (!map[parentId].lastReplyAt || new Date(t) > new Date(map[parentId].lastReplyAt))) {
                map[parentId].lastReplyAt = t;
            }
        });
        return map;
    }, [deferredMessages]);
    const sendMutation = useSendMessage(chatId);
    const editMutation = useEditMessage(chatId);
    const deleteMutation = useDeleteMessage(chatId);
    const addReactionMutation = useAddReaction(chatId);
    const removeReactionMutation = useRemoveReaction(chatId);
    const forwardMutation = useForwardMessages(chatId);
    const updateThreadTitleMutation = useUpdateThreadTitle(chatId);

    const [threadParentId, setThreadParentId] = useState(null);
    const [threadTitleEditing, setThreadTitleEditing] = useState(false);
    const [threadTitleDraft, setThreadTitleDraft] = useState('');
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editingMessageOriginalText, setEditingMessageOriginalText] = useState('');
    /** When set, the message being edited belongs to a thread and uses the drawer composer. */
    const [editingParentMessageId, setEditingParentMessageId] = useState(null);
    const editingParentMessageIdRef = useRef(null);
    const [selectedMessageIds, setSelectedMessageIds] = useState(() => new Set());
    const selectionAnchorRef = useRef(null);
    const [selectionMode, setSelectionMode] = useState(false);
    const [forwardModalOpen, setForwardModalOpen] = useState(false);
    const [forwardTargetChatIds, setForwardTargetChatIds] = useState([]);
    const [forwardTargetUserIds, setForwardTargetUserIds] = useState([]);
    const [forwardSearch, setForwardSearch] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchQuery, setSearchQuery] = useState(''); // draft input
    const [searchTerm, setSearchTerm] = useState(''); // committed term (Enter)
    // Open search mode when navigated from sidebar action.
    // Must react even when user is already in the same chat (chatId doesn't change).
    useEffect(() => {
        if (!chatId) return;
        const flag = location?.state?.openChatSearch;
        if (!flag) return;
        setIsSearching(true);
        setSearchQuery('');
        setSearchTerm('');
        // Clear the navigation state so refresh/back doesn't keep reopening.
        navigate(location.pathname, { replace: true, state: null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId, location?.state?.openChatSearch, location?.pathname]);

    // Also allow sidebar to trigger search without route/state changes.
    useEffect(() => {
        const onOpen = (e) => {
            const targetId = e?.detail?.chatId;
            if (!targetId) return;
            if (String(targetId) !== String(chatId)) return;
            setIsSearching(true);
            setSearchQuery('');
            setSearchTerm('');
        };
        window.addEventListener('chat:open-search', onOpen);
        return () => window.removeEventListener('chat:open-search', onOpen);
    }, [chatId]);

    const [exitNotice, setExitNotice] = useState('');

    useEffect(() => {
        const onExited = (e) => {
            const targetId = e?.detail?.chatId;
            if (!targetId) return;
            if (String(targetId) !== String(chatId)) return;
            setExitNotice('You exited this group. Select another conversation from the left.');
            const prefix = location?.pathname?.startsWith('/tenant') ? '/tenant' : '';
            navigate(`${prefix}/chat`);
        };
        window.addEventListener('chat:exited', onExited);
        return () => window.removeEventListener('chat:exited', onExited);
    }, [chatId, navigate, location?.pathname]);

    useEffect(() => {
        if (chatId) setExitNotice('');
    }, [chatId]);

    const [searchPage, setSearchPage] = useState(1);
    useEffect(() => {
        setSearchPage(1);
    }, [chatId, searchTerm, isSearching]);

    const {
        data: serverSearchData,
        isFetching: serverSearchFetching,
    } = useChatMessageSearch({
        chatId,
        term: searchTerm,
        page: searchPage,
        limit: 20,
        enabled: isSearching && !!String(searchTerm || '').trim(),
    });

    /** Local reply counts so we show "N replies" even when main chat API doesn't return thread replies */
    const [threadCountOverlay, setThreadCountOverlay] = useState(() => ({}));

    const {
        data: threadData,
        isLoading: threadRepliesLoading,
        hasNextPage: hasThreadNextPage,
        fetchNextPage: fetchThreadNextPage,
        isFetchingNextPage: isFetchingThreadNextPage,
    } = useThreadReplies(chatId, threadParentId);
    const threadReplies = threadData?.items ?? [];
    useEffect(() => {
        editingParentMessageIdRef.current = editingParentMessageId;
    }, [editingParentMessageId]);

    const threadComposeRef = useRef(null);
    const threadScrollRef = useRef(null);
    const threadScrollRafRef = useRef(null);
    const threadScrollHeightBeforeLoadMore = useRef(null);
    const threadPrevRepliesLenRef = useRef(0);

    // When thread data has loaded, sync overlay from thread API totals (not placeholder/stale list length).
    useEffect(() => {
        if (!threadParentId) return;
        if (threadRepliesLoading) return;
        const apiTotal = threadData?.total;
        const loaded = threadReplies.length;
        const count =
            typeof apiTotal === 'number' && apiTotal >= 0 ? Math.max(apiTotal, loaded) : loaded;
        const latest = loaded > 0 ? threadReplies[loaded - 1] : null;
        const lastReplyAt = latest?.createdAt ?? latest?.created_at ?? null;
        setThreadCountOverlay((prev) => ({
            ...prev,
            [threadParentId]: { count, lastReplyAt },
        }));
    }, [threadParentId, threadRepliesLoading, threadData?.total, threadData?.items]);

    const messageListRef = useRef(null);
    const composeRef = useRef(null);
    const scrollHeightBeforeLoadMore = useRef(null);
    const topBounceHideTimeoutRef = useRef(null);
    const prevScrollTopRef = useRef(null);
    const [showTopBounce, setShowTopBounce] = useState(false);
    const [newMessageCount, setNewMessageCount] = useState(0);
    const [showJumpToBottom, setShowJumpToBottom] = useState(false);
    const showJumpToBottomRef = useRef(false);
    const isNearBottomRef = useRef(true);
    // When we "force scroll to latest" (send/new message), keep pinning briefly so late layout
    // changes (images/markdown) don't leave us slightly above the true bottom.
    const pinToBottomUntilRef = useRef(0);
    const pinToBottomRafRef = useRef(null);
    const initialScrolledChatRef = useRef(null);
    const prevMessagesLenRef = useRef(0);
    const prependStartLenRef = useRef(null);
    const { connected, connecting, error } = useChatSocketStatus();
    const scrollRafRef = useRef(null);

    // Clear unsent draft when switching chats (avoid carrying text across conversations).
    useEffect(() => {
        composeRef.current?.clear?.();
        threadComposeRef.current?.clear?.();
        setEditingMessageId(null);
        setEditingMessageOriginalText('');
        setEditingParentMessageId(null);
        setThreadParentId(null);
        setThreadTitleEditing(false);
        setThreadTitleDraft('');
        setSelectedMessageIds(new Set());
        selectionAnchorRef.current = null;
        setSelectionMode(false);
        setForwardModalOpen(false);
        setForwardTargetChatIds([]);
        setForwardTargetUserIds([]);
        setForwardSearch('');
        setIsSearching(false);
        setSearchQuery('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId]);

    const scrollToBottom = useCallback(() => {
        const el = messageListRef.current;
        if (!el) return;
        const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
        el.scrollTop = maxTop;
    }, []);

    const scrollThreadToBottom = useCallback(() => {
        const el = threadScrollRef.current;
        if (!el) return;
        el.scrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
    }, []);

    const pinToBottomFor = useCallback(
        (ms = 650) => {
            pinToBottomUntilRef.current = Date.now() + ms;
            if (pinToBottomRafRef.current) cancelAnimationFrame(pinToBottomRafRef.current);
            const tick = () => {
                const until = pinToBottomUntilRef.current;
                if (!until || Date.now() > until) {
                    pinToBottomRafRef.current = null;
                    return;
                }
                scrollToBottom();
                pinToBottomRafRef.current = requestAnimationFrame(tick);
            };
            pinToBottomRafRef.current = requestAnimationFrame(tick);
        },
        [scrollToBottom],
    );

    const computeIsNearBottom = useCallback(() => {
        const el = messageListRef.current;
        if (!el) return true;
        return el.scrollHeight - el.scrollTop - el.clientHeight < 150;
    }, []);

    // Initial entry: always jump to bottom (latest messages).
    useLayoutEffect(() => {
        if (!chatId) return;
        if (messagesLoading) return;
        if (String(initialScrolledChatRef.current) === String(chatId)) return;
        // Wait until we actually have messages rendered before marking as "scrolled".
        // Otherwise we can miss the first real data render and stay near top.
        const len = messages?.length ?? 0;
        if (len === 0) return;
        initialScrolledChatRef.current = chatId;
        setNewMessageCount(0);
        showJumpToBottomRef.current = false;
        setShowJumpToBottom(false);
        prevMessagesLenRef.current = len;

        // Ensure layout has settled (flex spacer + images) before scrolling.
        const raf1 = requestAnimationFrame(() => {
            scrollToBottom();
            const raf2 = requestAnimationFrame(scrollToBottom);
            // Brief pin avoids "bottom gap" after late markdown/image layout.
            pinToBottomFor(650);
            return () => cancelAnimationFrame(raf2);
        });
        return () => cancelAnimationFrame(raf1);
    }, [chatId, messagesLoading, messages?.length, scrollToBottom, pinToBottomFor]);

    // New messages behavior (deterministic): only runs when actual message count changes.
    useEffect(() => {
        if (!chatId) return;
        if (messagesLoading) return;
        if (isFetchingNextPage) return;
        if (scrollHeightBeforeLoadMore.current != null) return;

        const len = messages?.length ?? 0;
        const prevLen = prevMessagesLenRef.current ?? 0;
        prevMessagesLenRef.current = len;
        if (len <= prevLen) return;

        // If we just loaded older messages (prepended), do NOT treat them as "new messages".
        if (prependStartLenRef.current != null && len > prependStartLenRef.current) {
            if (DEV) console.debug('[chat] ignore prepend length increase', { chatId, prevLen, len, prependStartLen: prependStartLenRef.current });
            prependStartLenRef.current = null;
            setNewMessageCount(0);
            return;
        }

        // Requirement update: always keep the view pinned to latest on new messages.
        // This ensures the newest message is always visible (no "stuck up" state).
        setNewMessageCount(0);
        showJumpToBottomRef.current = false;
        setShowJumpToBottom(false);
        // Multi-pass + brief pin so late layout changes don't leave a bottom gap.
        pinToBottomFor(800);
        requestAnimationFrame(scrollToBottom);
        requestAnimationFrame(() => requestAnimationFrame(scrollToBottom));
    }, [chatId, messagesLoading, isFetchingNextPage, messages?.length, computeIsNearBottom, scrollToBottom]);

    // If we are currently "pinned to bottom", keep bottom aligned when the list resizes
    // (e.g. images load, markdown reflow, font swap).
    useEffect(() => {
        const el = messageListRef.current;
        if (!el) return;
        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => {
            const until = pinToBottomUntilRef.current;
            if (until && Date.now() <= until) scrollToBottom();
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [scrollToBottom]);

    // Restore scroll position after loading more (prepended messages) – run synchronously before paint so we stay in place
    useLayoutEffect(() => {
        if (isFetchingNextPage) return;
        const prevHeight = scrollHeightBeforeLoadMore.current;
        if (prevHeight == null) return;
        const el = messageListRef.current;
        if (!el) {
            scrollHeightBeforeLoadMore.current = null;
            prependStartLenRef.current = null;
            return;
        }
        const delta = el.scrollHeight - prevHeight;
        if (delta > 0) el.scrollTop += delta;
        if (DEV) console.debug('[chat] scroll restored after prepend', { chatId, delta, scrollTop: el.scrollTop });
        scrollHeightBeforeLoadMore.current = null;
    }, [isFetchingNextPage, messages?.length]);

    const handleMessageListScroll = useCallback(() => {
        if (scrollRafRef.current) return;
        scrollRafRef.current = requestAnimationFrame(() => {
            scrollRafRef.current = null;
            const el = messageListRef.current;
            if (!el) return;
            const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
            isNearBottomRef.current = nearBottom;
            // User took control; stop forced pinning.
            if (!nearBottom) pinToBottomUntilRef.current = 0;
            if (nearBottom && newMessageCount) setNewMessageCount(0);
            const nextShowJump = !nearBottom;
            if (nextShowJump !== showJumpToBottomRef.current) {
                showJumpToBottomRef.current = nextShowJump;
                setShowJumpToBottom(nextShowJump);
            }

            if (hasNextPage && !isFetchingNextPage && el.scrollTop < 100) {
                scrollHeightBeforeLoadMore.current = el.scrollHeight;
                prependStartLenRef.current = messages?.length ?? 0;
                if (DEV) console.debug('[chat] pagination trigger (scroll top)', { chatId, scrollTop: el.scrollTop });
                fetchNextPage();
            }
            if (!hasNextPage && messages?.length > 0) {
                const st = el.scrollTop;
                const prev = prevScrollTopRef.current;
                if (st <= 8) {
                    if (prev == null || prev > 8) {
                        setShowTopBounce(true);
                        if (topBounceHideTimeoutRef.current) clearTimeout(topBounceHideTimeoutRef.current);
                        topBounceHideTimeoutRef.current = setTimeout(() => {
                            setShowTopBounce(false);
                            topBounceHideTimeoutRef.current = null;
                        }, 2500);
                    }
                } else if (st > 24) {
                    setShowTopBounce(false);
                    if (topBounceHideTimeoutRef.current) {
                        clearTimeout(topBounceHideTimeoutRef.current);
                        topBounceHideTimeoutRef.current = null;
                    }
                }
                prevScrollTopRef.current = st;
            }
        });
    }, [hasNextPage, isFetchingNextPage, fetchNextPage, messages?.length, newMessageCount]);

    const handleThreadScroll = useCallback(() => {
        if (threadScrollRafRef.current) return;
        threadScrollRafRef.current = requestAnimationFrame(() => {
            threadScrollRafRef.current = null;
            const el = threadScrollRef.current;
            if (!el) return;
            if (hasThreadNextPage && !isFetchingThreadNextPage && el.scrollTop < 100) {
                threadScrollHeightBeforeLoadMore.current = el.scrollHeight;
                fetchThreadNextPage();
            }
        });
    }, [hasThreadNextPage, isFetchingThreadNextPage, fetchThreadNextPage]);

    useLayoutEffect(() => {
        if (isFetchingThreadNextPage) return;
        const prevHeight = threadScrollHeightBeforeLoadMore.current;
        if (prevHeight == null) return;
        const el = threadScrollRef.current;
        if (!el) {
            threadScrollHeightBeforeLoadMore.current = null;
            return;
        }
        const delta = el.scrollHeight - prevHeight;
        if (delta > 0) el.scrollTop += delta;
        threadScrollHeightBeforeLoadMore.current = null;
    }, [isFetchingThreadNextPage, threadReplies.length]);

    useLayoutEffect(() => {
        if (!threadParentId) return;
        if (threadRepliesLoading) return;
        const el = threadScrollRef.current;
        if (!el) return;
        const tick = () => {
            el.scrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
        };
        tick();
        requestAnimationFrame(tick);
        requestAnimationFrame(() => requestAnimationFrame(tick));
        setTimeout(tick, 150);
    }, [threadParentId, threadRepliesLoading]);

    useLayoutEffect(() => {
        if (!editingMessageId || !editingParentMessageId) return;
        if (String(threadParentId) !== String(editingParentMessageId)) return;
        if (threadRepliesLoading) return;
        const t = editingMessageOriginalText ?? '';
        threadComposeRef.current?.setPlainText?.(t);
    }, [
        editingMessageId,
        editingParentMessageId,
        threadParentId,
        threadRepliesLoading,
        editingMessageOriginalText,
    ]);

    useEffect(() => {
        threadPrevRepliesLenRef.current = 0;
    }, [threadParentId]);

    useEffect(() => {
        if (!threadParentId) return;
        const n = threadReplies.length;
        const prev = threadPrevRepliesLenRef.current;
        threadPrevRepliesLenRef.current = n;
        if (n > prev) {
            scrollThreadToBottom();
            requestAnimationFrame(scrollThreadToBottom);
            setTimeout(scrollThreadToBottom, 120);
        }
    }, [threadParentId, threadReplies.length, scrollThreadToBottom]);

    useEffect(() => {
        return () => {
            if (topBounceHideTimeoutRef.current) clearTimeout(topBounceHideTimeoutRef.current);
            if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
            if (pinToBottomRafRef.current) cancelAnimationFrame(pinToBottomRafRef.current);
            if (threadScrollRafRef.current) cancelAnimationFrame(threadScrollRafRef.current);
        };
    }, []);

    const handleSend = useCallback(
        (content = '', files = [], linkAttachments = []) => {
            if (!chatId || sendMutation.isPending || editMutation.isPending) return;
            const text = (content || '').trim();
            const fileList = Array.isArray(files) ? files : [];
            const links = Array.isArray(linkAttachments) ? linkAttachments : [];
            const hasContent = text || fileList.length > 0 || links.length > 0;
            if (!hasContent) return;
            if (editingMessageId) {
                // Edit mode: text-only (no files/links) for now.
                const isThreadComposerEdit = !!editingParentMessageId;
                editMutation.mutate(
                    { msgId: editingMessageId, text: content },
                    {
                        onSuccess: () => {
                            setEditingMessageId(null);
                            setEditingMessageOriginalText('');
                            setEditingParentMessageId(null);
                            if (isThreadComposerEdit) {
                                threadComposeRef.current?.clear?.();
                            } else {
                                composeRef.current?.clear?.();
                            }
                            antMessage.success('Message edited');
                        },
                    },
                );
                return;
            }

            sendMutation.mutate(
                { text: content, files: fileList, linkAttachments: links },
                {
                    onSuccess: () => {
                        composeRef.current?.clear?.();
                        // Ensure the new outgoing message is fully visible (layout + images).
                        pinToBottomFor(900);
                        requestAnimationFrame(scrollToBottom);
                        requestAnimationFrame(() => requestAnimationFrame(scrollToBottom));
                        setTimeout(scrollToBottom, 150);
                    },
                },
            );
        },
        [
            chatId,
            sendMutation,
            editMutation,
            editingMessageId,
            editingParentMessageId,
            pinToBottomFor,
            scrollToBottom,
        ],
    );

    const handleReplyClick = useCallback((parentId) => setThreadParentId(parentId), []);
    const handleThreadSend = useCallback((content = '', files = [], linkAttachments = []) => {
        if (!chatId || !threadParentId || sendMutation.isPending || editMutation.isPending) return;
        const text = (content || '').trim();
        const fileList = Array.isArray(files) ? files : [];
        const links = Array.isArray(linkAttachments) ? linkAttachments : [];
        const hasContent = text || fileList.length > 0 || links.length > 0;
        if (!hasContent) return;
        sendMutation.mutate(
            { text: content, parentMessageId: threadParentId, files: fileList, linkAttachments: links },
            {
                onSuccess: () => {
                    threadComposeRef.current?.clear?.();
                    setThreadCountOverlay((prev) => {
                        const current = prev[threadParentId]?.count ?? 0;
                        return {
                            ...prev,
                            [threadParentId]: {
                                count: current + 1,
                                lastReplyAt: new Date().toISOString(),
                            },
                        };
                    });
                    scrollThreadToBottom();
                    requestAnimationFrame(scrollThreadToBottom);
                    requestAnimationFrame(() => requestAnimationFrame(scrollThreadToBottom));
                    setTimeout(scrollThreadToBottom, 200);
                },
            },
        );
    }, [chatId, threadParentId, sendMutation, editMutation, scrollThreadToBottom]);

    const handleThreadComposerSend = useCallback(
        (content = '', files = [], linkAttachments = []) => {
            if (
                editingMessageId &&
                editingParentMessageId &&
                String(threadParentId) === String(editingParentMessageId)
            ) {
                return handleSend(content, files, linkAttachments);
            }
            return handleThreadSend(content, files, linkAttachments);
        },
        [editingMessageId, editingParentMessageId, threadParentId, handleSend, handleThreadSend],
    );

    const truncateThreadTitle = useCallback((s, max = 48) => {
        const t = String(s ?? '').replace(/\s+/g, ' ').trim();
        if (!t) return 'Thread';
        if (t.length <= max) return t;
        return `${t.slice(0, max - 1)}…`;
    }, []);

    const handleEditClick = useCallback((msg) => {
        const id = msg.id ?? msg._id;
        const text = getMessageText(msg);
        if (!id) return;
        const parentId = msg.parentMessageId ?? msg.parent_message_id;
        if (parentId) {
            const pid = String(parentId);
            setEditingParentMessageId(pid);
            setThreadParentId(pid);
            setEditingMessageId(String(id));
            setEditingMessageOriginalText(text);
            return;
        }
        setEditingParentMessageId(null);
        setEditingMessageId(id);
        setEditingMessageOriginalText(text);
        composeRef.current?.setPlainText?.(text);
    }, []);

    /** Thread API may omit parent on each reply; always treat drawer rows as replies to `threadParentId`. */
    const handleEditInThreadDrawer = useCallback(
        (msg) => {
            if (!threadParentId) {
                handleEditClick(msg);
                return;
            }
            handleEditClick({
                ...msg,
                parentMessageId: msg.parentMessageId ?? msg.parent_message_id ?? threadParentId,
            });
        },
        [handleEditClick, threadParentId],
    );

    const handleDeleteClick = useCallback(
        (msgId) => {
            Modal.confirm({
                title: 'Delete message?',
                content: 'This cannot be undone.',
                maskClosable: false,
                okText: 'Delete',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk: () =>
                    deleteMutation.mutate(msgId, {
                        onSuccess: () => antMessage.success('Message deleted'),
                    }),
            });
        },
        [deleteMutation],
    );
    const handleAddReaction = useCallback(
        ({ messageId, emoji }) => {
            if (!messageId || !emoji) return;
            // Make the selection obvious immediately.
            queryClient.setQueryData(['chat', 'detail', chatId], (prev) => {
                if (!prev) return prev;
                const messagesObj = prev.messages ?? {};
                const items = Array.isArray(messagesObj.items) ? messagesObj.items : [];
                const nextItems = items.map((m) => {
                    const mid = m.id ?? m._id;
                    if (String(mid) !== String(messageId)) return m;
                    return { ...m, myReactionEmoji: emoji };
                });
                return { ...prev, messages: { ...messagesObj, items: nextItems } };
            });
            addReactionMutation.mutate(
                { messageId, emoji },
                {
                    onError: () => {
                        queryClient.setQueryData(['chat', 'detail', chatId], (prev) => {
                            if (!prev) return prev;
                            const messagesObj = prev.messages ?? {};
                            const items = Array.isArray(messagesObj.items) ? messagesObj.items : [];
                            const nextItems = items.map((m) => {
                                const mid = m.id ?? m._id;
                                if (String(mid) !== String(messageId)) return m;
                                return { ...m, myReactionEmoji: null };
                            });
                            return { ...prev, messages: { ...messagesObj, items: nextItems } };
                        });
                    },
                },
            );
        },
        [addReactionMutation, chatId, queryClient],
    );

    const handleToggleReaction = useCallback(
        ({ messageId, emoji, reactedByMe, myReactionEmoji }) => {
            if (!messageId || !emoji) return;

            // Optimistic UI: immediately reflect which reaction is mine, so "remove" is obvious.
            // We store a lightweight `myReactionEmoji` on the message cache.
            const updateCache = (nextEmoji) => {
                queryClient.setQueryData(['chat', 'detail', chatId], (prev) => {
                    if (!prev) return prev;
                    const messages = prev.messages ?? {};
                    const items = Array.isArray(messages.items) ? messages.items : [];
                    const nextItems = items.map((m) => {
                        const mid = m.id ?? m._id;
                        if (String(mid) !== String(messageId)) return m;
                        return { ...m, myReactionEmoji: nextEmoji };
                    });
                    return { ...prev, messages: { ...messages, items: nextItems } };
                });
            };

            // If we KNOW it's mine (selected), toggle off.
            if ((myReactionEmoji && String(myReactionEmoji) === String(emoji)) || reactedByMe) {
                updateCache(null);
                removeReactionMutation.mutate(
                    { messageId, emoji },
                    {
                        onError: () => updateCache(emoji),
                    },
                );
                return;
            }

            // If backend doesn't tell us who's reacted, we might be "double-clicking" to remove.
            // Fix: try remove first; if it fails (not found), then add.
            updateCache(emoji);
            removeReactionMutation.mutate(
                { messageId, emoji },
                {
                    onSuccess: () => updateCache(null),
                    onError: () => {
                        addReactionMutation.mutate(
                            { messageId, emoji },
                            {
                                onError: () => updateCache(null),
                            },
                        )
                    },
                },
            );
        },
        [addReactionMutation, removeReactionMutation, chatId, queryClient],
    );

    /** Normalize message.reactions to grouped [{ emoji, count, userIds?, users? }] */
    const getMessageReactions = useCallback((msg) => {
        const r = msg?.reactions ?? msg?.reactionCounts;
        if (!r) return [];
        // Object map from backend: { "👍": 3 } or { "👍": { count, userIds, users } }
        if (!Array.isArray(r)) {
            return Object.entries(r).map(([emoji, val]) =>
                typeof val === 'number' ? { emoji, count: val } : { emoji, ...val },
            );
        }

        // Array from backend: one entry per reaction (or already grouped) — group by emoji.
        const byEmoji = new Map();
        for (const item of r) {
            if (!item || !item.emoji) continue;
            const key = String(item.emoji);
            let acc = byEmoji.get(key);
            if (!acc) {
                acc = {
                    emoji: item.emoji,
                    count: 0,
                    userIds: [],
                    users: [],
                };
                byEmoji.set(key, acc);
            }

            // Merge count.
            const explicitCount =
                typeof item.count === 'number' && Number.isFinite(item.count) && item.count > 0
                    ? item.count
                    : null;
            const inferredCount =
                explicitCount != null
                    ? explicitCount
                    : Array.isArray(item.userIds) && item.userIds.length > 0
                        ? item.userIds.length
                        : 1;
            acc.count += inferredCount;

            // Merge users / userIds when provided.
            const itemUsers = Array.isArray(item.users) ? item.users : [];
            for (const u of itemUsers) {
                if (!u) continue;
                const userId = u.userId ?? u.id ?? null;
                const userName = u.userName ?? u.name ?? null;
                const userEmail = u.userEmail ?? u.email ?? null;
                acc.users.push({ userId, userName, userEmail });
                if (userId != null) acc.userIds.push(String(userId));
            }

            if (Array.isArray(item.userIds) && item.userIds.length > 0) {
                for (const id of item.userIds) {
                    if (id == null) continue;
                    acc.userIds.push(String(id));
                }
            } else {
                const singleUserId = item.userId ?? item.id ?? null;
                const singleUserName = item.userName ?? item.name ?? null;
                const singleUserEmail = item.userEmail ?? item.email ?? null;
                if (singleUserId != null || singleUserName != null || singleUserEmail != null) {
                    acc.users.push({
                        userId: singleUserId ?? null,
                        userName: singleUserName ?? null,
                        userEmail: singleUserEmail ?? null,
                    });
                    if (singleUserId != null) acc.userIds.push(String(singleUserId));
                }
            }
        }
        return Array.from(byEmoji.values());
    }, []);

    const chat = getChatFromResponse(chatData);

    const renameChatMutation = useRenameChat(chatId);
    const addMembersMutation = useAddChatMembers(chatId);
    const removeMemberMutation = useRemoveChatMember(chatId);

    const [renameOpen, setRenameOpen] = useState(false);
    const [renameDraft, setRenameDraft] = useState('');
    const [addOpen, setAddOpen] = useState(false);
    const [addSelectedUserIds, setAddSelectedUserIds] = useState([]);
    const [removeOpen, setRemoveOpen] = useState(false);
    const [chatAssetsOpen, setChatAssetsOpen] = useState(false);
    const [chatAssetsTab, setChatAssetsTab] = useState('media');
    // Members are displayed inline in the chat header (no modal).

    const groupAdminId = useMemo(() => {
        return (
            chat?.createdById ??
            chat?.createdBy ??
            chat?.ownerId ??
            chat?.owner_id ??
            chat?.adminId ??
            chat?.admin_id ??
            chat?.initiatorId ??
            chat?.initiator_id ??
            null
        );
    }, [chat]);

    const memberListForModal = useMemo(() => {
        const parts = Array.isArray(chat?.participants) ? chat.participants : [];
        const out = [];
        const seen = new Set();
        parts.forEach((p) => {
            const id = p?.userId ?? p?.id;
            if (!id) return;
            const key = String(id);
            if (seen.has(key)) return;
            seen.add(key);
            out.push({
                id,
                name: p?.name ?? p?.email ?? 'User',
                email: p?.email ?? '',
                avatarUrl: p?.avatarUrl ?? p?.avatar ?? null,
            });
        });
        const admin = groupAdminId != null ? String(groupAdminId) : '';
        if (admin) {
            out.sort((a, b) => {
                const aAd = String(a.id) === admin;
                const bAd = String(b.id) === admin;
                if (aAd && !bAd) return -1;
                if (!aAd && bAd) return 1;
                return 0;
            });
        }
        return out;
    }, [chat?.participants, groupAdminId]);

    const existingMemberIds = useMemo(() => {
        const parts = Array.isArray(chat?.participants) ? chat.participants : [];
        return new Set(parts.map((p) => String(p?.userId ?? p?.id)).filter(Boolean));
    }, [chat?.participants]);

    const { data: groupEligibleUsersData, isLoading: groupEligibleUsersLoading } = useEligibleChatUsers({
        enabled: !!chatId && addOpen,
    });
    const groupEligibleUsersFlat = groupEligibleUsersData?.users ?? [];
    const addMemberOptions = useMemo(() => {
        return (groupEligibleUsersFlat ?? [])
            .map((u) => {
                const id = u?.id ?? u?.userId ?? u?._id;
                if (!id) return null;
                if (existingMemberIds.has(String(id))) return null;
                const name = u?.name ?? u?.fullName ?? u?.email ?? 'User';
                const email = u?.email ?? '';
                return { value: id, label: email ? `${name} (${email})` : name };
            })
            .filter(Boolean);
    }, [groupEligibleUsersFlat, existingMemberIds]);

    const membersForRemove = useMemo(() => {
        const parts = Array.isArray(chat?.participants) ? chat.participants : [];
        return parts
            .map((p) => ({
                id: p?.userId ?? p?.id,
                name: p?.name ?? p?.email ?? 'User',
                email: p?.email ?? '',
            }))
            .filter((p) => p.id && String(p.id) !== String(currentUserId));
    }, [chat?.participants, currentUserId]);
    const { data: chatListData } = useChatList({ limit: 50, enabled: !!chatId });
    const chatList = chatListData?.items ?? [];
    const chatFromList = useMemo(
        () => chatList.find((c) => (c.id ?? c._id) === chatId),
        [chatList, chatId],
    );
    const chatTitle = chatId && chat ? getChatDisplayName(chat, currentUserId) : 'Messages';
    const recipientName = chatId && chat ? getChatDisplayName(chat, currentUserId) : '';
    const currentUserName = currentUser?.name || currentUser?.email?.split('@')[0] || 'You';
    const headerAvatarUserId = useMemo(() => {
        const participants = chat?.participants ?? [];
        const other = participants.find((p) => String(p.userId ?? p.id) !== String(currentUserId));
        return other?.userId ?? other?.id ?? chatId ?? '';
    }, [chat?.participants, currentUserId, chatId]);
    const headerAvatarColor = getAvatarColor(headerAvatarUserId);
    const projectName =
        chat?.project?.name ??
        chat?.openMarketLead?.title ??
        chatFromList?.project?.name ??
        chatFromList?.openMarketLead?.title ??
        null;
    const projectStatus =
        chat?.project?.status ??
        chat?.openMarketLead?.status ??
        chatFromList?.project?.status ??
        chatFromList?.openMarketLead?.status ??
        null;
    const clientName = useMemo(() => {
        const c = chat?.client || chatFromList?.client || chat?.project?.client || chatFromList?.project?.client;
        if (typeof c === 'string' && c) return c;
        if (c?.name) return c.name;
        if (c?.fullName) return c.fullName;
        return (
            chat?.project?.clientName ??
            chatFromList?.project?.clientName ??
            chat?.clientName ??
            chatFromList?.clientName ??
            null
        );
    }, [chat, chatFromList]);

    const priorityTitle = useMemo(() => {
        if (projectName) return projectName;
        if (clientName) return clientName;

        const t = String(chat?.type || '').toLowerCase();
        const isGroup = t === 'group' || t === 'channel' || chat?.isChannel;
        if (isGroup && chat?.name) return chat.name;

        return 'Chat';
    }, [projectName, clientName, chat?.name, chat?.type, chat?.isChannel]);
    const participantEmail = useMemo(() => {
        const participants = chat?.participants ?? [];
        const other = participants.find((p) => String(p.userId ?? p.id) !== String(currentUserId));
        return other?.email ?? '';
    }, [chat?.participants, currentUserId]);

    const mentionUsers = useMemo(() => {
        const parts = Array.isArray(chat?.participants) ? chat.participants : [];
        return parts
            .map((p) => ({
                id: p.userId ?? p.id,
                userId: p.userId ?? p.id,
                name: p.name ?? '',
                email: p.email ?? '',
            }))
            .filter((p) => p.userId && String(p.userId) !== String(currentUserId));
    }, [chat?.participants, currentUserId]);

    const headerTitle = useMemo(() => {
        if (!chatId || !chat) return null;

        if (priorityTitle === 'Chat') {
            return <span style={{ fontWeight: 600 }}>Chat</span>;
        }

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'inherit' }}>
                <span style={{ opacity: 0.4, fontWeight: 400 }}>Chat</span>
                <span style={{ opacity: 0.3, fontSize: 13 }}>&gt;</span>
                <span style={{ fontWeight: 600 }}>{priorityTitle}</span>
                {projectStatus && <StatusBadge status={projectStatus} style={{ marginLeft: 4 }} />}
            </div>
        );
    }, [chatId, chat, priorityTitle, projectStatus]);

    useDocTitle({ location, projectName: priorityTitle });

    const isGroupChat = useMemo(() => {
        const t = String(chat?.type || '').toLowerCase();
        const isChannel = chat?.isChannel === true || t === 'channel' || String(chat?.name || '').trim().startsWith('#');
        return t === 'group' || isChannel;
    }, [chat?.type, chat?.isChannel, chat?.name]);
    const headerAvatarImageUrl = useMemo(
        () => (isGroupChat ? null : resolveDmHeaderAvatarUrl(chat, currentUserId)),
        [chat, currentUserId, isGroupChat],
    );
    const canManageGroup = useMemo(
        () => (isGroupChat ? canManageGroupChat(chat, currentUserId) : false),
        [isGroupChat, chat, currentUserId],
    );

    const groupCreatorDisplay = useMemo(() => {
        if (groupAdminId == null || groupAdminId === '') return 'Unknown';
        const row = memberListForModal.find((m) => String(m.id) === String(groupAdminId));
        return row?.name || row?.email || 'Unknown';
    }, [groupAdminId, memberListForModal]);

    const groupCreatedAtLabel = useMemo(() => {
        const raw = chat?.createdAt ?? chat?.created_at ?? null;
        if (raw == null || raw === '') return '';
        try {
            const n = typeof raw === 'number' ? (raw < 1e12 ? raw * 1000 : raw) : raw;
            const d = new Date(n);
            if (Number.isNaN(d.getTime())) return '';
            return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
        } catch {
            return '';
        }
    }, [chat]);

    const chatAssetsAboutContent = useMemo(() => {
        if (!isGroupChat) return null;
        return (
            <div style={{ padding: '8px 0 24px' }}>
                <Typography.Paragraph style={{ marginTop: 0, marginBottom: 8 }}>
                    <Typography.Text strong>Created by </Typography.Text>
                    <Typography.Text>{groupCreatorDisplay}</Typography.Text>
                    {groupCreatedAtLabel ? (
                        <Typography.Text type="secondary">
                            {' · '}
                            {groupCreatedAtLabel}
                        </Typography.Text>
                    ) : null}
                </Typography.Paragraph>
                <Typography.Text strong>Members ({memberListForModal.length})</Typography.Text>
                <List
                    style={{ marginTop: 12 }}
                    size="small"
                    dataSource={memberListForModal}
                    renderItem={(m) => {
                        const colors = getAvatarColor(String(m.id));
                        const isAd = groupAdminId && String(m.id) === String(groupAdminId);
                        const ini = String(m.name || m.email || '?')
                            .split(/\s+/)
                            .filter(Boolean)
                            .map((s) => s[0])
                            .join('')
                            .toUpperCase()
                            .slice(0, 2);
                        const memberAvatarUrl = resolveChatAvatarUrl(m.avatarUrl);
                        return (
                            <List.Item key={String(m.id)}>
                                <List.Item.Meta
                                    avatar={
                                        <ChatCircleAvatar
                                            imageUrl={memberAvatarUrl}
                                            initials={ini || '?'}
                                            size={36}
                                            avatarBg={colors.bg}
                                            avatarColor={colors.color}
                                            fontSize={13}
                                        />
                                    }
                                    title={
                                        <span>
                                            {m.name || m.email || 'Member'}
                                            {isAd ? (
                                                <Typography.Text type="secondary"> · Admin</Typography.Text>
                                            ) : null}
                                        </span>
                                    }
                                    description={m.email && m.name ? m.email : null}
                                />
                            </List.Item>
                        );
                    }}
                />
            </div>
        );
    }, [isGroupChat, groupCreatorDisplay, groupCreatedAtLabel, memberListForModal, groupAdminId]);

    const openChatFilesDrawer = useCallback(() => {
        setChatAssetsTab('media');
        setChatAssetsOpen(true);
    }, []);

    const openGroupDetailsDrawer = useCallback(() => {
        if (!isGroupChat) return;
        setChatAssetsTab('media');
        setChatAssetsOpen(true);
    }, [isGroupChat]);

    const headerSubtitle = useMemo(() => {
        if (!chatId || !chat) return '';
        const t = String(chat?.type || '').toLowerCase();
        if (t === 'group' || t === 'channel') {
            const count = (chat?.participants ?? []).length;
            return `${count} member${count === 1 ? '' : 's'}`;
        }
        return participantEmail || '';
    }, [chatId, chat, participantEmail]);

    const messagesById = useMemo(() => {
        const map = new Map();
        (deferredMessages ?? []).forEach((m) => {
            const id = m?.id ?? m?._id;
            if (!id) return;
            map.set(String(id), m);
        });
        return map;
    }, [deferredMessages]);

    const visibleMessages = useMemo(() => {
        const q = (searchTerm || '').trim().toLowerCase();
        const top = (deferredMessages ?? []).filter((m) => !(m.parentMessageId ?? m.parent_message_id));
        if (!q) return top;

        // When search UI is open, prefer server-side search results.
        const serverItems = serverSearchData?.items;
        if (isSearching && Array.isArray(serverItems)) {
            return serverItems.filter((m) => !(m.parentMessageId ?? m.parent_message_id));
        }

        // Fallback: local filter.
        return top.filter((m) => String(getMessageText(m) || '').toLowerCase().includes(q));
    }, [deferredMessages, searchTerm, serverSearchData?.items, isSearching]);

    const visibleTimeline = useMemo(() => {
        const out = [];
        let lastDay = '';
        (visibleMessages ?? []).forEach((m, idx) => {
            const ts = m.createdAt ?? m.created_at ?? null;
            const k = ts ? dayKey(ts) : '';
            if (k && k !== lastDay) {
                lastDay = k;
                out.push({ __type: 'day', key: `day-${k}`, label: formatChatDayLabel(ts) });
            }
            out.push({ __type: 'msg', key: `msg-${m.id ?? m._id ?? idx}`, msg: m });
        });
        return out;
    }, [visibleMessages]);

    const threadsForTopBar = useMemo(() => {
        const list = [];
        const seen = new Set();

        (deferredMessages ?? []).forEach((m) => {
            const id = m?.id ?? m?._id;
            if (!id) return;
            const key = String(id);
            if (seen.has(key)) return;

            const serverRep = m?.replyCount ?? m?.reply_count;
            const overlayCount = threadCountOverlay[key]?.count;
            const derivedCount = threadCountByParent[key]?.count;
            const count =
                typeof serverRep === 'number' && serverRep > 0
                    ? serverRep
                    : overlayCount ??
                    derivedCount ??
                    (typeof serverRep === 'number' ? serverRep : 0) ??
                    (Array.isArray(m?.replies) ? m.replies.length : 0) ??
                    0;

            const serverTitle = String(m?.threadTitle ?? m?.thread_title ?? '').trim();
            const hasThread = count > 0 || serverTitle.length > 0;
            if (!hasThread) return;

            seen.add(key);
            list.push({
                id: key,
                title: serverTitle || 'Untitled Thread',
                count: typeof count === 'number' ? count : 0,
                lastTs: (() => {
                    const last =
                        threadCountOverlay[key]?.lastReplyAt ??
                        threadCountByParent[key]?.lastReplyAt ??
                        m?.updatedAt ??
                        m?.createdAt ??
                        null;
                    return last ? new Date(last).getTime() : 0;
                })(),
            });
        });

        list.sort((a, b) => (b.lastTs ?? 0) - (a.lastTs ?? 0));
        return list;
    }, [deferredMessages, threadCountOverlay, threadCountByParent]);

    const clearSelection = useCallback(() => {
        setSelectedMessageIds(new Set());
        selectionAnchorRef.current = null;
        setSelectionMode(false);
        setForwardModalOpen(false);
        setForwardTargetChatIds([]);
        setForwardTargetUserIds([]);
        setForwardSearch('');
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (isSearching) {
                    setIsSearching(false);
                    setSearchQuery('');
                }
                clearSelection();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [clearSelection, isSearching]);

    const handleMessageClick = useCallback(
        (e, msgId) => {
            e.stopPropagation();
            const isMulti = e.ctrlKey || e.metaKey;
            const isRange = e.shiftKey;

            setSelectedMessageIds((prev) => {
                const next = new Set(prev);

                if (isRange && selectionAnchorRef.current) {
                    const ids = visibleMessages.map((m) => m.id ?? m._id).filter(Boolean);
                    const a = ids.findIndex((x) => String(x) === String(selectionAnchorRef.current));
                    const b = ids.findIndex((x) => String(x) === String(msgId));
                    if (a !== -1 && b !== -1) {
                        const [start, end] = a < b ? [a, b] : [b, a];
                        if (!isMulti) next.clear();
                        for (let i = start; i <= end; i += 1) next.add(ids[i]);
                        return next;
                    }
                }

                if (isMulti) {
                    if (next.has(msgId)) next.delete(msgId);
                    else next.add(msgId);
                } else {
                    next.clear();
                    next.add(msgId);
                }

                selectionAnchorRef.current = msgId;
                return next;
            });
        },
        [visibleMessages],
    );

    const enterSelectionMode = useCallback(
        (msgId) => {
            if (!msgId) return;
            setSelectionMode(true);
            setSelectedMessageIds((prev) => {
                const next = new Set(prev);
                next.add(msgId);
                selectionAnchorRef.current = msgId;
                return next;
            });
        },
        [],
    );

    const openForwardModal = useCallback(() => {
        if (selectedMessageIds.size === 0) {
            antMessage.info('Select at least 1 message to forward');
            return;
        }
        setForwardModalOpen(true);
    }, [selectedMessageIds.size]);

    const submitForward = useCallback(() => {
        const messageIds = Array.from(selectedMessageIds);
        if (messageIds.length === 0) return;
        if (forwardTargetChatIds.length === 0 && forwardTargetUserIds.length === 0) {
            antMessage.error('Select at least 1 target chat or user');
            return;
        }
        forwardMutation.mutate(
            {
                messageIds,
                targetChatIds: forwardTargetChatIds,
                targetUserIds: forwardTargetUserIds,
            },
            {
                onSuccess: () => {
                    antMessage.success('Forwarded');
                    setForwardModalOpen(false);
                    clearSelection();
                },
                onError: (err) => {
                    const msg =
                        err?.response?.data?.message ||
                        err?.message ||
                        'Forward failed';
                    antMessage.error(msg);
                },
            },
        );
    }, [
        selectedMessageIds,
        forwardTargetChatIds,
        forwardTargetUserIds,
        forwardMutation,
        clearSelection,
    ]);

    const { data: eligibleUsersData, isLoading: eligibleUsersLoading } = useEligibleChatUsers({
        enabled: forwardModalOpen,
    });
    const eligibleUsersFlat = eligibleUsersData?.users ?? [];

    const filteredForwardChats = useMemo(() => {
        const q = String(forwardSearch || '').trim().toLowerCase();
        const list = (chatList ?? []).filter((c) => String(c.id ?? c._id) !== String(chatId));
        if (!q) return list;
        return list.filter((c) => {
            const label = (c.name || (c.type === 'dm' ? 'Direct message' : 'Group') || '').toLowerCase();
            const last = (c.lastMessage?.content?.text || c.lastMessage?.content?.title || '').toLowerCase();
            return label.includes(q) || last.includes(q);
        });
    }, [forwardSearch, chatList, chatId]);

    const filteredForwardUsers = useMemo(() => {
        const q = String(forwardSearch || '').trim().toLowerCase();
        if (!q) return eligibleUsersFlat;
        return (eligibleUsersFlat ?? []).filter((u) => {
            const name = String(u?.name || '').toLowerCase();
            const email = String(u?.email || '').toLowerCase();
            return name.includes(q) || email.includes(q);
        });
    }, [forwardSearch, eligibleUsersFlat]);

    return (
        <ChatFullHeightWrap>
            <ChatLayout>
                <ChatMessagePanel>
                    {!chatId ? (
                        <EmptyStateFill>{exitNotice || 'Select a conversation from the left'}</EmptyStateFill>
                    ) : (
                        <>
                            <div
                                style={{
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 20,
                                    background: 'transparent',
                                    backdropFilter: 'blur(20px)',
                                    WebkitBackdropFilter: 'blur(20px)',
                                    borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
                                }}
                            >
                                <ChatHeader
                                    projectName={projectName}
                                    projectStatus={projectStatus}
                                    title={headerTitle}
                                    subtitle={headerSubtitle}
                                    initials={getInitials(recipientName || chatTitle)}
                                    avatarImageUrl={headerAvatarImageUrl}
                                    avatarColor={headerAvatarColor}
                                    selectionCount={selectedMessageIds.size}
                                    isSearching={isSearching}
                                    searchValue={searchQuery}
                                    onSearchChange={(val) => setSearchQuery(val)}
                                    onSearchSubmit={() => {
                                        const term = String(searchQuery || '').trim();
                                        setSearchTerm(term);
                                    }}
                                    onSearchToggle={() => {
                                        setIsSearching((s) => !s);
                                        setSearchQuery('');
                                        setSearchTerm('');
                                    }}
                                    isGroup={isGroupChat}
                                    canManageGroup={canManageGroup}
                                    members={memberListForModal}
                                    membersCount={memberListForModal.length}
                                    adminId={groupAdminId}
                                    onRenameGroup={() => {
                                        setRenameDraft(String(chat?.name || '').trim());
                                        setRenameOpen(true);
                                    }}
                                    onAddMember={() => {
                                        setAddSelectedUserIds([]);
                                        setAddOpen(true);
                                    }}
                                    onRemoveMember={() => setRemoveOpen(true)}
                                    onOpenChatFiles={openChatFilesDrawer}
                                    onOpenGroupSidePanel={isGroupChat ? openGroupDetailsDrawer : undefined}
                                    onPin={() => {
                                        if (selectedMessageIds.size === 0) return;
                                        antMessage.info('Pin selected messages UI next');
                                    }}
                                    onInfo={() => {
                                        if (selectedMessageIds.size === 0) return;
                                        antMessage.info('Selected message info UI next');
                                    }}
                                />
                            </div>

                            <ChatAssetsDrawer
                                open={chatAssetsOpen}
                                onClose={() => setChatAssetsOpen(false)}
                                chatId={chatId}
                                title={chatTitle || 'Chat'}
                                defaultTab={chatAssetsTab}
                                showAboutTab={isGroupChat}
                                aboutContent={chatAssetsAboutContent}
                            />

                            <Modal
                                title="Rename group"
                                open={renameOpen}
                                onCancel={() => setRenameOpen(false)}
                                onOk={() => {
                                    const name = String(renameDraft || '').trim();
                                    if (!name) return antMessage.error('Enter a group name');
                                    renameChatMutation.mutate(
                                        { name },
                                        {
                                            onSuccess: () => {
                                                antMessage.success('Group renamed');
                                                setRenameOpen(false);
                                            },
                                            onError: (err) => {
                                                const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to rename group';
                                                antMessage.error(msg);
                                            },
                                        },
                                    );
                                }}
                                okButtonProps={{ loading: renameChatMutation.isPending }}
                            >
                                <Input value={renameDraft} onChange={(e) => setRenameDraft(e.target.value)} placeholder="Group name" />
                            </Modal>

                            <Modal
                                title="Add member"
                                open={addOpen}
                                onCancel={() => setAddOpen(false)}
                                onOk={() => {
                                    const ids = Array.isArray(addSelectedUserIds) ? addSelectedUserIds.filter(Boolean) : [];
                                    if (ids.length === 0) return antMessage.error('Select at least 1 user');
                                    addMembersMutation.mutate(
                                        { memberIds: ids },
                                        {
                                            onSuccess: () => {
                                                antMessage.success('Members added');
                                                setAddOpen(false);
                                            },
                                            onError: (err) => {
                                                const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to add members';
                                                antMessage.error(msg);
                                            },
                                        },
                                    );
                                }}
                                okButtonProps={{ loading: addMembersMutation.isPending }}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    <div style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>
                                        Select users to add (existing members are hidden).
                                    </div>
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        loading={groupEligibleUsersLoading}
                                        value={addSelectedUserIds}
                                        onChange={(vals) => setAddSelectedUserIds(vals)}
                                        placeholder="Search users…"
                                        options={addMemberOptions}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </Modal>

                            <Modal
                                title="Remove member"
                                open={removeOpen}
                                onCancel={() => setRemoveOpen(false)}
                                footer={null}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {membersForRemove.length === 0 ? (
                                        <div style={{ color: 'var(--ant-color-text-secondary)' }}>No removable members.</div>
                                    ) : (
                                        membersForRemove.map((m) => (
                                            <div
                                                key={String(m.id)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 12,
                                                    padding: '10px 12px',
                                                    borderRadius: 10,
                                                    border: '1px solid rgba(0,0,0,0.08)',
                                                }}
                                            >
                                                <div style={{ minWidth: 0 }}>
                                                    <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {m.name}
                                                    </div>
                                                    {m.email ? (
                                                        <div style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>{m.email}</div>
                                                    ) : null}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        removeMemberMutation.mutate(
                                                            { userId: m.id },
                                                            {
                                                                onSuccess: () => antMessage.success('Member removed'),
                                                                onError: (err) => {
                                                                    const msg =
                                                                        err?.response?.data?.message ??
                                                                        err?.message ??
                                                                        'Failed to remove member';
                                                                    antMessage.error(msg);
                                                                },
                                                            },
                                                        );
                                                    }}
                                                    style={{
                                                        border: '1px solid rgba(239,68,68,0.35)',
                                                        background: 'rgba(239,68,68,0.08)',
                                                        color: '#ef4444',
                                                        borderRadius: 10,
                                                        padding: '6px 10px',
                                                        cursor: 'pointer',
                                                        fontWeight: 800,
                                                    }}
                                                    disabled={removeMemberMutation.isPending}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </Modal>
                            <ThreadTopBar
                                threads={threadsForTopBar}
                                selectedThreadId={threadParentId}
                                onThreadTap={(t) => {
                                    const id = t?.id ?? t?.threadId ?? t?.parentId;
                                    if (!id) return;
                                    setThreadParentId(String(id));
                                }}
                                hasMoreChatPages={hasNextPage}
                                onLoadMoreChatPages={fetchNextPage}
                                isLoadingMoreChatPages={isFetchingNextPage}
                            />
                            <ChatMessageListWrap
                                onDragOver={(e) => {
                                    // Only claim the drop zone for file drags; unconditional preventDefault
                                    // can interfere with text selection / link drag behavior in Chromium.
                                    const types = e.dataTransfer?.types ? Array.from(e.dataTransfer.types) : [];
                                    if (types.includes('Files')) e.preventDefault();
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
                                    if (files.length === 0) return;
                                    composeRef.current?.addFiles?.(files);
                                    composeRef.current?.focus?.();
                                }}
                            >
                                {messagesLoading && visibleTimeline.length === 0 ? (
                                    <DetailPanelLoading
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        <Spin size="large" />
                                    </DetailPanelLoading>
                                ) : null}
                                {newMessageCount > 0 && (
                                    <BottomNewMessageBadgeWrap>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setNewMessageCount(0);
                                                showJumpToBottomRef.current = false;
                                                setShowJumpToBottom(false);
                                                scrollToBottom();
                                            }}
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                padding: 0,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <StatusBadge status="primary" size="small">
                                                {newMessageCount} new message{newMessageCount === 1 ? '' : 's'}
                                            </StatusBadge>
                                        </button>
                                    </BottomNewMessageBadgeWrap>
                                )}
                                {showJumpToBottom && (
                                    <BottomJumpToLatestWrap>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setNewMessageCount(0);
                                                showJumpToBottomRef.current = false;
                                                setShowJumpToBottom(false);
                                                scrollToBottom();
                                            }}
                                            aria-label="Scroll to latest messages"
                                            style={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: 999,
                                                border: `1px solid ${dark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)'}`,
                                                background: dark ? 'rgba(37,37,37,0.75)' : 'rgba(255,255,255,0.90)',
                                                color: dark ? 'rgba(255,255,255,0.88)' : 'rgba(0,0,0,0.78)',
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: dark ? '0 10px 24px rgba(0,0,0,0.35)' : '0 10px 24px rgba(2,6,23,0.12)',
                                                backdropFilter: 'blur(6px)',
                                            }}
                                        >
                                            <DownOutlined />
                                        </button>
                                    </BottomJumpToLatestWrap>
                                )}
                                {showTopBounce && !hasNextPage && messages.length > 0 && (
                                    <TopBounceBadgeWrap>
                                        <StatusBadge status="primary" size="small">
                                            Beginning of conversation
                                        </StatusBadge>
                                    </TopBounceBadgeWrap>
                                )}
                                <ChatMessageList
                                    ref={messageListRef}
                                    onScroll={handleMessageListScroll}
                                    onClick={() => {
                                        if (!selectionMode) return;
                                        clearSelection();
                                    }}
                                >
                                    {/* Spacer pushes messages to bottom when list is short, but doesn't block scrolling */}
                                    <div style={{ flex: '1 1 auto' }} />
                                    {visibleMessages.length === 0 ? (
                                        <EmptyStateFill>No message found</EmptyStateFill>
                                    ) : (
                                        visibleTimeline.map((item, idx) => {
                                            if (item.__type === 'day') {
                                                return (
                                                    <div
                                                        key={item.key}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 12,
                                                            margin: '6px 0',
                                                            opacity: 0.8,
                                                        }}
                                                    >
                                                        <div style={{ height: 1, background: dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)', flex: 1 }} />
                                                        <div
                                                            style={{
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                                padding: '4px 10px',
                                                                borderRadius: 999,
                                                                border: `1px solid ${dark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)'}`,
                                                                background: dark ? 'rgba(255,255,255,0.04)' : 'rgba(2,6,23,0.03)',
                                                                color: dark ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.65)',
                                                                userSelect: 'none',
                                                            }}
                                                        >
                                                            {item.label}
                                                        </div>
                                                        <div style={{ height: 1, background: dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)', flex: 1 }} />
                                                    </div>
                                                );
                                            }
                                            const msg = item.msg;
                                            const msgId = msg.id ?? msg._id ?? `${idx}-msg`;
                                            const reactions = getMessageReactions(msg);
                                            return (
                                                <ChatMessageRow
                                                    key={msgId}
                                                    msg={msg}
                                                    idxKey={msgId}
                                                    participants={chat?.participants ?? []}
                                                    currentUserId={currentUserId}
                                                    currentUserName={currentUserName}
                                                    dark={dark}
                                                    reactions={reactions}
                                                    selectionMode={selectionMode}
                                                    isSelected={selectedMessageIds.has(msgId)}
                                                    onSelectClick={handleMessageClick}
                                                    onSelectToggle={(id) => {
                                                        setSelectedMessageIds((prev) => {
                                                            const next = new Set(prev);
                                                            if (next.has(id)) next.delete(id);
                                                            else next.add(id);
                                                            selectionAnchorRef.current = id;
                                                            return next;
                                                        });
                                                    }}
                                                    onReplyClick={handleReplyClick}
                                                    onAddReaction={handleAddReaction}
                                                    onToggleReaction={handleToggleReaction}
                                                    onEditClick={handleEditClick}
                                                    onDeleteClick={handleDeleteClick}
                                                    onForwardSelect={enterSelectionMode}
                                                    addReactionLoading={addReactionMutation.isPending}
                                                    threadCountByParent={threadCountByParent}
                                                    threadCountOverlay={threadCountOverlay}
                                                />
                                            );
                                        })
                                    )}
                                    {typingUsers.length > 0 && (
                                        <TypingBubbleWrap>
                                            <TypingBubble>
                                                <span className="typing-dot" />
                                                <span className="typing-dot" />
                                                <span className="typing-dot" />
                                            </TypingBubble>
                                            <span
                                                style={{
                                                    fontSize: 12,
                                                    color: dark ? 'rgba(255,255,255,0.5)' : '#888',
                                                    marginLeft: 4,
                                                }}
                                            >
                                                {typingUsers.map((u) => u.userName || 'Someone').join(', ')} is typing
                                            </span>
                                        </TypingBubbleWrap>
                                    )}
                                </ChatMessageList>
                            </ChatMessageListWrap>
                            <Container
                                $flex="0 0 auto"
                                $display="flex"
                                $flexDirection="column"
                                $padding="12px 20px 16px"
                                $gap="10px"
                                style={{
                                    position: 'sticky',
                                    bottom: 0,
                                    zIndex: 20,
                                    background: dark ? 'rgba(31,31,31,0.4)' : 'rgba(255,255,255,0.4)',
                                    backdropFilter: 'blur(12px)',
                                    WebkitBackdropFilter: 'blur(12px)',
                                    borderTop: dark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #eee',
                                }}
                            >
                                {selectionMode && selectedMessageIds.size > 0 && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 12,
                                            padding: '8px 10px',
                                            borderRadius: 10,
                                            border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
                                            background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
                                            color: dark ? 'rgba(255,255,255,0.92)' : '#111',
                                            fontSize: 12,
                                        }}
                                    >
                                        <div style={{ fontWeight: 700 }}>
                                            {selectedMessageIds.size} selected
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <button
                                                type="button"
                                                onClick={openForwardModal}
                                                style={{
                                                    border: 'none',
                                                    background: dark ? 'rgba(139,92,246,0.85)' : 'rgba(139,92,246,0.9)',
                                                    color: '#fff',
                                                    borderRadius: 8,
                                                    padding: '6px 10px',
                                                    cursor: 'pointer',
                                                    fontWeight: 800,
                                                }}
                                            >
                                                Forward
                                            </button>
                                            <button
                                                type="button"
                                                onClick={clearSelection}
                                                style={{
                                                    border: 'none',
                                                    background: 'transparent',
                                                    cursor: 'pointer',
                                                    fontWeight: 800,
                                                    color: dark ? '#fff' : '#111',
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {editingMessageId && !editingParentMessageId && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 12,
                                            padding: '8px 10px',
                                            borderRadius: 10,
                                            border: `1px solid ${dark ? 'rgba(139,92,246,0.35)' : 'rgba(139,92,246,0.25)'}`,
                                            background: dark ? 'rgba(139,92,246,0.12)' : 'rgba(139,92,246,0.08)',
                                            color: dark ? 'rgba(255,255,255,0.9)' : '#111',
                                            fontSize: 12,
                                        }}
                                    >
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <div style={{ fontWeight: 700, marginBottom: 2 }}>Editing message</div>
                                            <div
                                                style={{
                                                    opacity: 0.8,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {plainTextPreviewFromChatBody(editingMessageOriginalText).trim() || '—'}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setEditingMessageId(null);
                                                setEditingMessageOriginalText('');
                                                setEditingParentMessageId(null);
                                                composeRef.current?.clear?.();
                                            }}
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                cursor: 'pointer',
                                                fontWeight: 700,
                                                color: dark ? '#fff' : '#111',
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                )}
                                <MessageCompose
                                    ref={composeRef}
                                    onSend={handleSend}
                                    placeholder={recipientName ? `Message ${recipientName}` : 'Type a message...'}
                                    disabled={sendMutation.isPending || editMutation.isPending}
                                    onTypingStart={chatId ? () => emitTypingStart(chatId) : undefined}
                                    onTypingStop={chatId ? () => emitTypingStop(chatId) : undefined}
                                    socketStatus={{ connected, connecting, error }}
                                    mentionUsers={mentionUsers}
                                    hashtagTags={publicTags}
                                />
                            </Container>

                            <Modal
                                title="Forward messages"
                                open={forwardModalOpen}
                                onCancel={clearSelection}
                                onOk={submitForward}
                                okText="Forward"
                                confirmLoading={forwardMutation.isPending}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <div style={{ fontSize: 12, opacity: 0.85 }}>
                                        Forwarding {selectedMessageIds.size} message(s)
                                    </div>
                                    <Input
                                        value={forwardSearch}
                                        onChange={(e) => setForwardSearch(e.target.value)}
                                        placeholder="Search chats or users"
                                    />
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: 12,
                                            minHeight: 320,
                                        }}
                                    >
                                        <div
                                            style={{
                                                border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
                                                borderRadius: 10,
                                                padding: 10,
                                                minHeight: 0,
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>My chats</div>
                                            <div style={{ overflow: 'auto', maxHeight: 260, paddingRight: 6 }}>
                                                {filteredForwardChats.map((c) => {
                                                    const cid = c.id ?? c._id;
                                                    const checked = forwardTargetChatIds.includes(cid);
                                                    const label = c.name || (c.type === 'dm' ? 'Direct message' : 'Group');
                                                    return (
                                                        <label
                                                            key={cid}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 10,
                                                                padding: '8px 6px',
                                                                borderRadius: 8,
                                                                cursor: 'pointer',
                                                                background: checked
                                                                    ? dark
                                                                        ? 'rgba(139,92,246,0.18)'
                                                                        : 'rgba(139,92,246,0.12)'
                                                                    : 'transparent',
                                                            }}
                                                        >
                                                            <Checkbox
                                                                checked={checked}
                                                                onChange={() => {
                                                                    setForwardTargetChatIds((prev) => {
                                                                        const next = new Set(prev);
                                                                        if (next.has(cid)) next.delete(cid);
                                                                        else next.add(cid);
                                                                        return Array.from(next);
                                                                    });
                                                                }}
                                                            />
                                                            <div style={{ minWidth: 0 }}>
                                                                <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {label}
                                                                </div>
                                                                <div style={{ fontSize: 11, opacity: 0.7 }}>
                                                                    {c.type === 'dm' ? 'DM' : 'Group'}
                                                                </div>
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                                {filteredForwardChats.length === 0 ? (
                                                    <div style={{ fontSize: 12, opacity: 0.7, padding: 8 }}>No chats found</div>
                                                ) : null}
                                            </div>
                                        </div>

                                        <div
                                            style={{
                                                border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
                                                borderRadius: 10,
                                                padding: 10,
                                                minHeight: 0,
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>Eligible users</div>
                                            <div style={{ overflow: 'auto', maxHeight: 260, paddingRight: 6 }}>
                                                {eligibleUsersLoading ? (
                                                    <div style={{ fontSize: 12, opacity: 0.7, padding: 8 }}>Loading...</div>
                                                ) : (
                                                    filteredForwardUsers.map((u) => {
                                                        const checked = forwardTargetUserIds.includes(u.id);
                                                        return (
                                                            <label
                                                                key={u.id}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 10,
                                                                    padding: '8px 6px',
                                                                    borderRadius: 8,
                                                                    cursor: 'pointer',
                                                                    background: checked
                                                                        ? dark
                                                                            ? 'rgba(139,92,246,0.18)'
                                                                            : 'rgba(139,92,246,0.12)'
                                                                        : 'transparent',
                                                                }}
                                                            >
                                                                <Checkbox
                                                                    checked={checked}
                                                                    onChange={() => {
                                                                        setForwardTargetUserIds((prev) => {
                                                                            const next = new Set(prev);
                                                                            if (next.has(u.id)) next.delete(u.id);
                                                                            else next.add(u.id);
                                                                            return Array.from(next);
                                                                        });
                                                                    }}
                                                                />
                                                                <div style={{ minWidth: 0 }}>
                                                                    <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                        {u.name || u.email || u.id}
                                                                    </div>
                                                                    {u.email ? (
                                                                        <div style={{ fontSize: 11, opacity: 0.7, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                            {u.email}
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            </label>
                                                        );
                                                    })
                                                )}
                                                {!eligibleUsersLoading && filteredForwardUsers.length === 0 ? (
                                                    <div style={{ fontSize: 12, opacity: 0.7, padding: 8 }}>No users found</div>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Modal>

                            {/* Thread reply side panel (Slack-like) */}
                            <Drawer
                                title={() => {
                                    // Title is rendered inside drawer body header (Slack-like) so keep this simple.
                                    return 'Thread';
                                }}
                                placement="right"
                                width={400}
                                open={!!threadParentId}
                                onClose={() => {
                                    const wasThreadEdit = !!editingParentMessageIdRef.current;
                                    setThreadParentId(null);
                                    setThreadTitleEditing(false);
                                    setThreadTitleDraft('');
                                    setEditingParentMessageId(null);
                                    if (wasThreadEdit) {
                                        setEditingMessageId(null);
                                        setEditingMessageOriginalText('');
                                    }
                                    threadComposeRef.current?.clear?.();
                                }}
                                styles={{
                                    body: {
                                        padding: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        minHeight: 0,
                                        height: '100%',
                                        overflow: 'hidden',
                                    },
                                }}
                            >
                                {threadParentId &&
                                    (() => {
                                        const parentMsg = messages.find((m) => (m.id ?? m._id) === threadParentId);
                                        const participants = chat?.participants ?? [];
                                        if (!parentMsg) return null;
                                        const parentSenderName = getSenderDisplayName(
                                            parentMsg,
                                            currentUserId,
                                            currentUserName,
                                            participants,
                                        );
                                        const parentTime = formatMessageTime(
                                            parentMsg.createdAt ?? parentMsg.created_at,
                                        );
                                        const parentText = getMessageText(parentMsg);
                                        const parentAttachments =
                                            parentMsg?.content?.attachments ?? parentMsg?.attachments ?? [];
                                        const parentPreviewImages =
                                            parentMsg?.previewImages ??
                                            parentMsg?.preview_images ??
                                            parentMsg?.content?.previewImages ??
                                            parentMsg?.content?.preview_images;
                                        const parentInitials =
                                            parentSenderName
                                                .split(/\s+/)
                                                .map((s) => s[0])
                                                .filter(Boolean)
                                                .join('')
                                                .toUpperCase()
                                                .slice(0, 2) || '?';
                                        const serverThreadTitle = parentMsg.threadTitle ?? parentMsg.thread_title ?? null;
                                        const parentPlainTitle = plainTextPreviewFromChatBody(parentText);
                                        const computedDefaultTitle = truncateThreadTitle(parentPlainTitle, 54);
                                        const threadTitleToShow = serverThreadTitle
                                            ? truncateThreadTitle(plainTextPreviewFromChatBody(serverThreadTitle), 54)
                                            : computedDefaultTitle;
                                        const hasAnyReplies =
                                            (threadCountOverlay[threadParentId]?.count ?? threadCountByParent[threadParentId]?.count ?? parentMsg.replyCount ?? parentMsg.reply_count ?? 0) >
                                            0 ||
                                            threadReplies.length > 0;
                                        return (
                                            <div
                                                style={{
                                                    flex: 1,
                                                    minHeight: 0,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    overflow: 'hidden',
                                                    padding: 16,
                                                    boxSizing: 'border-box',
                                                }}
                                            >
                                                <div
                                                    ref={threadScrollRef}
                                                    onScroll={handleThreadScroll}
                                                    style={{
                                                        flex: '1 1 0%',
                                                        minHeight: 0,
                                                        overflowY: 'auto',
                                                        overflowX: 'hidden',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 12,
                                                        WebkitOverflowScrolling: 'touch',
                                                        position: 'relative',
                                                    }}
                                                >
                                                    {isFetchingThreadNextPage ? (
                                                        <LoadMoreOverlay>
                                                            <Spin size="small" />
                                                            <span>Loading earlier replies…</span>
                                                        </LoadMoreOverlay>
                                                    ) : null}
                                                    <div
                                                        style={{
                                                            position: 'sticky',
                                                            top: 0,
                                                            zIndex: 1,
                                                            flexShrink: 0,
                                                            marginBottom: 8,
                                                            paddingBottom: 10,
                                                            borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
                                                                }`,
                                                            background: dark ? '#141414' : '#fff',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                gap: 8,
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                                                    {threadTitleEditing ? (
                                                                        <Input
                                                                            value={threadTitleDraft}
                                                                            onChange={(e) => setThreadTitleDraft(e.target.value)}
                                                                            placeholder="Thread title"
                                                                            onPressEnter={() => {
                                                                                const next = String(threadTitleDraft || '').trim();
                                                                                if (!next) {
                                                                                    setThreadTitleEditing(false);
                                                                                    setThreadTitleDraft('');
                                                                                    return;
                                                                                }
                                                                                updateThreadTitleMutation.mutate(
                                                                                    { msgId: threadParentId, threadTitle: next },
                                                                                    {
                                                                                        onSuccess: () => {
                                                                                            antMessage.success('Thread title updated');
                                                                                            setThreadTitleEditing(false);
                                                                                        },
                                                                                        onError: (err) => {
                                                                                            antMessage.error(err?.response?.data?.message || err?.message || 'Failed to update thread title');
                                                                                        },
                                                                                    },
                                                                                );
                                                                            }}
                                                                            autoFocus
                                                                            size="small"
                                                                        />
                                                                    ) : (
                                                                        <span
                                                                            style={{
                                                                                fontSize: 13,
                                                                                fontWeight: 800,
                                                                                color: dark ? '#fff' : '#111',
                                                                                whiteSpace: 'nowrap',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                            }}
                                                                            title={threadTitleToShow}
                                                                        >
                                                                            {threadTitleToShow}
                                                                        </span>
                                                                    )}

                                                                    {hasAnyReplies && !threadTitleEditing ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setThreadTitleDraft(
                                                                                    String(
                                                                                        serverThreadTitle ||
                                                                                        plainTextPreviewFromChatBody(
                                                                                            parentText,
                                                                                        ) ||
                                                                                        '',
                                                                                    ).trim(),
                                                                                );
                                                                                setThreadTitleEditing(true);
                                                                            }}
                                                                            style={{
                                                                                border: 'none',
                                                                                background: 'transparent',
                                                                                cursor: 'pointer',
                                                                                color: dark ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.65)',
                                                                                fontWeight: 800,
                                                                                padding: 0,
                                                                            }}
                                                                            title="Edit thread title"
                                                                        >
                                                                            Edit
                                                                        </button>
                                                                    ) : null}
                                                                </div>
                                                                <span
                                                                    style={{
                                                                        fontSize: 12,
                                                                        color: dark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)',
                                                                    }}
                                                                >
                                                                    {parentSenderName}
                                                                </span>
                                                            </div>
                                                            <span
                                                                style={{
                                                                    fontSize: 12,
                                                                    color: dark
                                                                        ? 'rgba(255,255,255,0.5)'
                                                                        : 'rgba(0,0,0,0.5)',
                                                                }}
                                                            >
                                                                {parentTime}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div style={{ width: '100%', minWidth: 0, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                        <ChatThreadParentMessageBody body={parentText} dark={dark} />
                                                        <ChatMessageAttachmentStack
                                                            body={parentText}
                                                            attachments={parentAttachments}
                                                            messagePreviewImages={parentPreviewImages}
                                                            messageType={parentMsg?.type}
                                                            dark={dark}
                                                            previewContextMeta={{
                                                                senderName: parentSenderName,
                                                                timeLabel: parentTime,
                                                                initials: parentInitials,
                                                            }}
                                                            listMaxWidth="100%"
                                                        />
                                                    </div>
                                                    {(threadReplies.length > 0 || hasAnyReplies) && (
                                                        <Divider
                                                            style={{
                                                                margin: '8px 0 4px',
                                                                borderColor: dark ? 'rgba(255,255,255,0.12)' : 'rgba(15, 23, 42, 0.12)',
                                                            }}
                                                        />
                                                    )}
                                                    {threadReplies.map((reply) => (
                                                        <ChatMessageRow
                                                            key={reply.id ?? reply._id}
                                                            msg={reply}
                                                            idxKey={reply.id ?? reply._id}
                                                            participants={participants}
                                                            currentUserId={currentUserId}
                                                            currentUserName={currentUserName}
                                                            dark={dark}
                                                            selectionMode={false}
                                                            isSelected={false}
                                                            onReplyClick={handleReplyClick}
                                                            onAddReaction={handleAddReaction}
                                                            onToggleReaction={handleToggleReaction}
                                                            onEditClick={handleEditInThreadDrawer}
                                                            onDeleteClick={handleDeleteClick}
                                                            onForwardSelect={enterSelectionMode}
                                                            addReactionLoading={addReactionMutation.isPending}
                                                            threadCountByParent={threadCountByParent}
                                                            threadCountOverlay={threadCountOverlay}
                                                            disableThreadReply
                                                        />
                                                    ))}
                                                </div>
                                                <Container
                                                    $flex="0 0 auto"
                                                    $padding="12px 0 0"
                                                    $display="flex"
                                                    $flexDirection="column"
                                                    $gap="8px"
                                                >
                                                    {editingMessageId && editingParentMessageId && (
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                gap: 12,
                                                                padding: '8px 10px',
                                                                borderRadius: 10,
                                                                border: `1px solid ${dark ? 'rgba(139,92,246,0.35)' : 'rgba(139,92,246,0.25)'}`,
                                                                background: dark
                                                                    ? 'rgba(139,92,246,0.12)'
                                                                    : 'rgba(139,92,246,0.08)',
                                                                color: dark ? 'rgba(255,255,255,0.9)' : '#111',
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                                <div style={{ fontWeight: 700, marginBottom: 2 }}>
                                                                    Editing message
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        opacity: 0.8,
                                                                        whiteSpace: 'nowrap',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                    }}
                                                                >
                                                                    {plainTextPreviewFromChatBody(editingMessageOriginalText).trim() || '—'}
                                                                </div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setEditingMessageId(null);
                                                                    setEditingMessageOriginalText('');
                                                                    setEditingParentMessageId(null);
                                                                    threadComposeRef.current?.clear?.();
                                                                }}
                                                                style={{
                                                                    border: 'none',
                                                                    background: 'transparent',
                                                                    cursor: 'pointer',
                                                                    fontWeight: 700,
                                                                    color: dark ? '#fff' : '#111',
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    )}
                                                    <MessageCompose
                                                        ref={threadComposeRef}
                                                        onSend={handleThreadComposerSend}
                                                        placeholder="Reply in thread..."
                                                        disabled={sendMutation.isPending || editMutation.isPending}
                                                        mentionUsers={mentionUsers}
                                                        hashtagTags={publicTags}
                                                    />
                                                </Container>
                                            </div>
                                        );
                                    })()}
                            </Drawer>

                            {/* Edit is handled inline in composer now */}
                        </>
                    )}
                </ChatMessagePanel>
            </ChatLayout>
        </ChatFullHeightWrap>
    );
};

export default Chat;
